import type { ReactNode } from 'react';

import { HelpIcon } from './icons';

/**
 * «¿Cómo funciona esta pantalla?»: three or four lines in the shop's words.
 *
 * It exists because the person using this is not going to read a manual: the explanation
 * has to be one click away from the thing it explains. What changed on 2026-09-14 is
 * where it sits: a screen puts its help in `PageHeader`'s `help` prop (a round `?` next to
 * the title, opening a popover) and gets the content out of the way. `HelpBox` stays for
 * the places that still want it inline, and renders compact: a line of 13 px text with
 * the icon, and the panel only when opened. No full-width card above the content.
 */
export function HelpBox({
  title = '¿Cómo funciona esta pantalla?',
  items,
  children,
}: {
  title?: string;
  items?: ReactNode[];
  children?: ReactNode;
}) {
  return (
    <details className="group text-[13px]">
      <summary className="text-ink-3 hover:text-ink inline-flex cursor-pointer list-none items-center gap-1.5 font-medium select-none [&::-webkit-details-marker]:hidden">
        <HelpIcon size={15} className="text-accent shrink-0" />
        {title}
        <span aria-hidden="true" className="text-ink-4 text-[10px] group-open:rotate-180">
          ▾
        </span>
      </summary>
      <div className="border-line bg-card text-ink-2 mt-2 max-w-2xl rounded-md border px-4 py-3 text-[13.5px] leading-relaxed">
        <HelpContent items={items}>{children}</HelpContent>
      </div>
    </details>
  );
}

/** The body of a help panel: a bullet list when given `items`, whatever else otherwise. */
export function HelpContent({
  items,
  children,
}: {
  items?: ReactNode[] | undefined;
  children?: ReactNode;
}) {
  return (
    <>
      {items ? (
        <ul className="flex list-disc flex-col gap-1.5 pl-5">
          {items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ) : null}
      {children}
    </>
  );
}
